import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { normalizePhoneWithPlusPrefix } from "@/core/utils/phone";
import { CUSTOMER_STATE } from "@/customer/constants";
import type { ICustomer, CustomerUpdateParams } from "@/customer/customer";
import {
  customerKey,
  customersKey,
  invalidateCustomerQueries,
} from "@/customer/query/keys";
import { getEnrichedCustomer } from "@/customer/utils";
import { payoutsKey } from "@/payouts/query";
import { referralsKey, useGetReferrals } from "@/referrals/query";
import {
  businessTextMessagesKey,
  customerTextMessagesKey,
} from "@/text-messages/query";

export const useUpsertCustomer = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { data: referralsData } = useGetReferrals(businessId);

  return useMutation({
    mutationFn: (customerUpdate: CustomerUpdateParams) => {
      const preparedCustomerUpdate = {
        ...customerUpdate,
        phone_number: normalizePhoneWithPlusPrefix(
          customerUpdate.phone_number || "",
        ),
      };

      return (
        customerUpdate.id
          ? api.put(`/customers/${customerUpdate.id}`, preparedCustomerUpdate)
          : api.post("/customers", {
              ...preparedCustomerUpdate,
              business_id: businessId,
              onboard: customerUpdate.onboard || undefined,
            })
      ).then((response) => response?.data);
    },
    onSuccess: (data, variables) => {
      const queryKeys = [
        customersKey(businessId, false), // without payouts
        customersKey(businessId, true), // with payouts
      ];

      const newCustomer = getEnrichedCustomer(data);
      const lead_referral = referralsData?.find(
        (referral) => referral.potential_customer?.id === newCustomer.id,
      );

      queryKeys.forEach((queryKey) => {
        queryClient.setQueryData<ICustomer[]>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          if (variables.id) {
            return oldData.map((customer) =>
              customer.id === variables.id
                ? {
                    ...(customer as ICustomer),
                    ...newCustomer,
                    lead_referral,
                  }
                : customer,
            );
          } else {
            if (queryKey[2]) {
              return [
                ...oldData,
                {
                  ...(newCustomer as ICustomer),
                  payouts: [],
                  lead_referral,
                },
              ];
            }
            return [
              ...oldData,
              {
                ...(newCustomer as ICustomer),
                lead_referral,
              },
            ];
          }
        });
      });

      invalidateCustomerQueries(queryClient, businessId);
      void queryClient.invalidateQueries({
        queryKey: referralsKey(businessId),
      });
      void queryClient.invalidateQueries({ queryKey: payoutsKey(businessId) });
    },
  });
};

export const useOptOutCustomer = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerId: string) =>
      api.put(`/customers/${customerId}`, {
        state: CUSTOMER_STATE.opted_out,
      }),
    onSuccess: (_, customerId) => {
      invalidateCustomerQueries(queryClient, businessId);
      void queryClient.invalidateQueries({ queryKey: customerKey(customerId) });
      void queryClient.invalidateQueries({
        queryKey: businessTextMessagesKey(businessId),
      });
      void queryClient.invalidateQueries({
        queryKey: customerTextMessagesKey(customerId),
      });
    },
  });
};
